/**
 * Threshold above which delivery counts as over-pacing.
 *
 * Mirrors `OVER_PACING_THRESHOLD_PCT` in
 * `apps/media-api/src/media-delivery/lib/serving-status.ts` - that is the one place
 * `pacingPct` is computed and compared to decide `serving_status`. Keep the two values
 * in step if either changes; this file has no way to import across apps to enforce it.
 */
export const OVER_PACING_THRESHOLD_PCT = 120;

/** Below this, delivery is meaningfully behind schedule rather than merely soft. */
const UNDER_PACING_THRESHOLD_PCT = 80;

export type PacingState = 'on_track' | 'under' | 'over' | 'not_pacing';

/**
 * Classifies an already-computed `pacingPct` for display. Does no pacing maths of its
 * own - `pacingPct` arrives from the API, computed once in `pacing.ts` on the backend.
 *
 * `null` means nothing was measured (no budget linked, or the pacing period has not
 * started) and must not collapse into `on_track`: that would assert a judgement about
 * delivery that was never made.
 */
export function pacingState(pacingPct: number | null): PacingState {
  if (pacingPct === null) return 'not_pacing';
  // Strict `>`, matching the backend: exactly at the threshold is not over.
  if (pacingPct > OVER_PACING_THRESHOLD_PCT) return 'over';
  if (pacingPct < UNDER_PACING_THRESHOLD_PCT) return 'under';
  return 'on_track';
}
